import React, { useRef, useState } from "react";
import { Button } from "./ui/button";
import { BadgeCheck, Upload, Camera, Clock, ShieldAlert, Loader2, FileWarning, FileText } from "lucide-react";
import { toast } from "sonner";
import { authAPI, uploadDocument } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CameraCapture from "./CameraCapture";

// GDPR note shown to the user:
// - The document is stored encrypted on our server only until admin review (max 48h)
// - After approval/rejection it is automatically deleted from disk
// - Only verification metadata (verified=true) is kept on your account.

export default function VerificationPanel() {
  const { user, refreshMe } = useAuth();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("id_card");
  const [cameraOpen, setCameraOpen] = useState(false);

  if (!user) return null;
  const status = user.verification_status || "none";

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File troppo grande (max 10MB)");
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif", "application/pdf"];
    if (!allowed.includes(file.type.toLowerCase())) {
      return toast.error("Carica una foto del documento (JPG/PNG/WEBP/HEIC) o un PDF");
    }
    setUploading(true);
    try {
      const { url } = await uploadDocument(file);
      await authAPI.submitVerification(url, docType);
      toast.success("Documento inviato", { description: "Verifica entro 48h. Riceverai una notifica." });
      if (refreshMe) await refreshMe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio documento");
    } finally {
      setUploading(false);
    }
  };

  // VERIFIED state
  if (user.is_verified || status === "verified") {
    return (
      <div className="bg-sky-50 border-2 border-sky-200 rounded-2xl p-5 flex items-start gap-4" data-testid="verification-panel-verified">
        <div className="shrink-0 w-12 h-12 rounded-xl bg-sky-500 text-white flex items-center justify-center">
          <BadgeCheck className="w-7 h-7 fill-white text-sky-500" strokeWidth={2.2}/>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-black text-sky-900">Profilo verificato</h3>
            <span className="text-[10px] font-bold uppercase tracking-wider bg-sky-500 text-white px-2 py-0.5 rounded-full">SAMMARINESE</span>
          </div>
          <p className="text-sm text-sky-800 mt-1">
            Hai confermato la tua identità. Il <strong>badge blu</strong> apparirà accanto al tuo nome sui tuoi annunci e nelle chat — gli altri utenti si fideranno di più.
          </p>
        </div>
      </div>
    );
  }

  // PENDING state
  if (status === "pending") {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-4" data-testid="verification-panel-pending">
        <div className="shrink-0 w-12 h-12 rounded-xl bg-amber-400 text-amber-900 flex items-center justify-center">
          <Clock className="w-6 h-6" />
        </div>
        <div>
          <h3 className="font-black text-amber-900">Verifica in corso</h3>
          <p className="text-sm text-amber-800 mt-1">
            Il tuo documento è in revisione. Ti notificheremo entro 48 ore. Nel frattempo puoi continuare ad usare il marketplace.
          </p>
        </div>
      </div>
    );
  }

  // NONE / REJECTED
  return (
    <div className="bg-white border-2 border-dashed border-sky-300 rounded-2xl p-5" data-testid="verification-panel">
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-12 h-12 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center">
          <ShieldAlert className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-black text-[#1d3d3a]">Ottieni il badge sammarinese</h3>
            <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700 bg-sky-100 px-2 py-0.5 rounded-full">Gratuito</span>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Verifica la tua residenza in Repubblica di San Marino con una foto del tuo documento d'identità. Aumenta la fiducia dei compratori e vendi più velocemente.
          </p>

          {status === "rejected" && user.verification_rejection_reason && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 flex items-start gap-2" data-testid="verification-rejection">
              <FileWarning className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <strong>Verifica precedente rifiutata.</strong> Motivo: {user.verification_rejection_reason}. Puoi caricare un nuovo documento qui sotto.
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="text-sm border border-slate-300 rounded-lg h-10 px-3 bg-white font-medium"
              data-testid="verification-doc-type"
            >
              <option value="id_card">Carta d'identità</option>
              <option value="passport">Passaporto</option>
              <option value="driver_license">Patente</option>
            </select>
            <Button
              onClick={() => setCameraOpen(true)}
              disabled={uploading}
              className="rounded-full bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="verification-camera-button"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Camera className="w-4 h-4 mr-2"/>}
              Scatta foto
            </Button>
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="rounded-full border-2 border-sky-200 text-sky-700 hover:bg-sky-50"
              data-testid="verification-upload-button"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Upload className="w-4 h-4 mr-2"/>}
              Carica file (foto o PDF)
            </Button>
            {/* Gallery / file picker incl. PDF (camera handled by in-app modal) */}
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf,.pdf"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {/* Legacy ref kept for compat — no longer attached to any input */}
            <span ref={cameraRef} hidden />
          </div>

          <CameraCapture
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            onCapture={(file) => handleFile(file)}
            hint={
              docType === "passport"
                ? "Allinea il passaporto aperto nella cornice"
                : docType === "driver_license"
                ? "Allinea la patente nella cornice"
                : "Allinea la carta d'identità nella cornice"
            }
          />

          <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
            <FileText className="w-3.5 h-3.5"/> Formati accettati: JPG, PNG, WEBP, HEIC, <strong>PDF</strong> · max 10MB
          </div>

          <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
            🔒 <strong>Privacy GDPR:</strong> il documento viene visualizzato solo dal team di verifica ed <strong>eliminato automaticamente</strong> dai nostri server subito dopo la revisione (max 48h). Conserviamo solo l'esito (verificato/non verificato).
          </p>
        </div>
      </div>
    </div>
  );
}
